import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { apiConfig } from '../authConfig';
import { api } from '../utils/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    console.log('🚨 AuthProvider: COMPONENT RENDERING/RE-RENDERING!');
    
    const ssoDisabled = process.env.REACT_APP_SSO_ENABLED === 'false';
    
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loggingIn, setLoggingIn] = useState(false);
    const [loginError, setLoginError] = useState(null);
    const [ssoEnabled] = useState(!ssoDisabled);

    // Function to trigger analytics fetch after login
    const triggerAnalyticsFetch = async () => {
        try {
            console.log('📊 AuthContext: Triggering analytics fetch after login');
            window.dispatchEvent(new CustomEvent('triggerAnalyticsFetch'));
            const response = await api.getStats();
            console.log('📊 AuthContext: Analytics fetched successfully:', response.data);
        } catch (error) {
            console.error('❌ AuthContext: Failed to fetch analytics after login:', error);
        }
    };

    // Initialize authentication - runs ONLY ONCE
    useEffect(() => {
        console.log('🔍 AuthContext: useEffect RUNNING - Starting initialization...');
        console.log('🔍 AuthContext: SSO enabled:', !ssoDisabled);
        
        // If SSO is disabled (local development), auto-authenticate
        if (ssoDisabled) {
            console.log('🔧 AuthContext: SSO disabled - auto-authenticating for local development');
            setUser({ name: 'Local Developer', email: 'dev@localhost', roles: ['User'] });
            setToken('local-dev-token');
            setLoading(false);
            return;
        }
        
        // A token handed over by the sign-in callback comes in the URL fragment
        // (#token=...), which is never sent to a server. It is taken out of the
        // address bar and the history entry straight away.
        const tokenFromUrl =
            new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token');
        if (tokenFromUrl) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Check localStorage first (a fresh token from the URL wins over a saved one)
        const savedToken = tokenFromUrl ? null : localStorage.getItem('auth_token');
        console.log('🔍 AuthContext: Checking localStorage for saved token:', savedToken ? `Found: ${savedToken.substring(0, 20)}...` : 'Not found');
        
        if (savedToken) {
            console.log('✅ AuthContext: Found saved token, setting token state');
            setToken(savedToken);
            fetch(`${apiConfig.baseUrl}/api/auth/user`, {
                headers: {
                    'Authorization': `Bearer ${savedToken}`,
                    'Content-Type': 'application/json',
                },
            })
            .then(response => {
                if (response.ok) {
                    return response.json();
                } else {
                    throw new Error('Invalid token');
                }
            })
            .then(userData => {
                console.log('✅ AuthContext: User authenticated from saved token:', userData.user?.email);
                setUser(userData.user);
                setLoading(false);
                triggerAnalyticsFetch();
            })
            .catch(error => {
                console.error('❌ AuthContext: Saved token invalid, clearing:', error);
                localStorage.removeItem('auth_token');
                setToken(null);
                setUser(null);
                setLoading(false);
            });
            return;
        }

        // Use the token from the URL
        if (tokenFromUrl) {
            console.log('✅ AuthContext: Token found in URL');
            setToken(tokenFromUrl);
            localStorage.setItem('auth_token', tokenFromUrl);

            fetch(`${apiConfig.baseUrl}/api/auth/user`, {
                headers: {
                    'Authorization': `Bearer ${tokenFromUrl}`,
                    'Content-Type': 'application/json',
                },
            })
            .then(response => {
                if (response.ok) {
                    return response.json();
                } else {
                    throw new Error('Invalid token');
                }
            })
            .then(userData => {
                console.log('✅ AuthContext: User authenticated from URL token');
                setUser(userData.user);
                setLoading(false);
                triggerAnalyticsFetch();
            })
            .catch(error => {
                console.error('❌ AuthContext: URL token invalid:', error);
                localStorage.removeItem('auth_token');
                setToken(null);
                setUser(null);
                setLoading(false);
            });
        } else {
            console.log('❌ AuthContext: No token found, user needs to login');
            setUser(null);
            setLoading(false);
        }
    }, []); // EMPTY DEPENDENCY ARRAY - RUNS ONLY ONCE

    const login = useCallback(async () => {
        // The backend can cold-start, so this request may take many seconds.
        // Abort rather than leave the button spinning forever.
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000);

        setLoggingIn(true);
        setLoginError(null);

        try {
            console.log('🔐 AuthContext: Starting Azure AD SSO login...');

            if (!apiConfig.baseUrl) {
                throw new Error('The app is misconfigured: no API URL was set at build time. Please contact IT.');
            }

            // fragment=1: this bundle reads the token from the URL fragment, not the query.
            const response = await fetch(`${apiConfig.baseUrl}/api/auth/login?fragment=1`, { signal: controller.signal });

            if (!response.ok) {
                throw new Error(`The sign-in service returned an error (HTTP ${response.status}). Please try again.`);
            }

            const data = await response.json();
            if (!data.authorization_url) {
                throw new Error('The sign-in service did not return an authorization URL. Please contact IT.');
            }

            console.log('🔐 Redirecting to Azure AD:', data.authorization_url);
            // Keep loggingIn true — we are navigating away and the button should stay busy.
            window.location.href = data.authorization_url;
        } catch (error) {
            // fetch() rejects with a TypeError for network/DNS/CORS failures, whose
            // message ("Failed to fetch") means nothing to an end user.
            const isNetworkError = error instanceof TypeError;
            const message = error.name === 'AbortError'
                ? 'The sign-in service is taking too long to respond. Please try again in a moment.'
                : isNetworkError
                    ? 'Could not reach the sign-in service. Please check your connection and try again — if this persists, contact IT.'
                    : (error.message || 'Could not start sign-in. Please try again.');

            console.error('❌ Login error:', error);
            setLoginError(message);
            setLoggingIn(false);
        } finally {
            clearTimeout(timeout);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            if (token) {
                await fetch(`${apiConfig.baseUrl}/api/auth/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('auth_token');
            setToken(null);
            setUser(null);
        }
    }, [token]);

    const getAccessToken = useCallback(async () => {
        return token;
    }, [token]);

    const hasRole = useCallback((role) => {
        return user?.roles?.includes(role) || false;
    }, [user]);

    const hasAnyRole = useCallback((roles) => {
        return roles.some(role => hasRole(role));
    }, [hasRole]);

    const isAdmin = useCallback(() => hasRole('User'), [hasRole]);
    const isHRUser = useCallback(() => hasRole('User'), [hasRole]);
    const canView = useCallback(() => hasRole('User'), [hasRole]);

    // Memoize the context value to prevent infinite re-renders
    const value = useMemo(() => ({
        user,
        token,
        loading,
        loggingIn,
        loginError,
        isAuthenticated: !!user && !!token,
        ssoEnabled,
        login,
        logout,
        getAccessToken,
        hasRole,
        hasAnyRole,
        isAdmin,
        isHRUser,
        canView
    }), [user, token, loading, loggingIn, loginError, ssoEnabled, login, logout, getAccessToken, hasRole, hasAnyRole, isAdmin, isHRUser, canView]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};